import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AsynchronousExecutionStatePill, AsynchronousExecutionStatusGuidance, asynchronousExecutionReasonLabel } from "../asynchronous-executions/asynchronous-execution-ui";
import type { useAsynchronousExecutions } from "./hooks/useAppBindings";

type Execution = ReturnType<typeof useAsynchronousExecutions>["items"][number];

/**
 * Shared "in-progress execution" panel for the batch screens. Resuming a
 * blocked execution tells the service the operator confirmed the blocker was
 * resolved, so the operator must say so explicitly before resuming.
 */
export function ActiveExecutionPanel({
  execution,
  kintoneAppId,
  busy,
  stateVerified,
  stateReadFailed,
  onRefresh,
  resumeLabel = "再開",
  cancelLabel = "取消",
  onResume,
  onCancel,
}: {
  execution: Execution;
  kintoneAppId?: string;
  busy: boolean;
  stateVerified: boolean;
  stateReadFailed: boolean;
  onRefresh(): void;
  resumeLabel?: string;
  cancelLabel?: string;
  onResume(): Promise<void>;
  onCancel(): Promise<void>;
}) {
  const [confirmedExecution, setConfirmedExecution] = useState<string | null>(null);
  const executionIdentity = `${execution.executionId}:${execution.revision}`;
  const resolutionConfirmed = confirmedExecution === executionIdentity;
  const actionsDisabled = busy || !stateVerified;
  useEffect(() => setConfirmedExecution(null), [executionIdentity]);
  useEffect(() => { if (stateReadFailed) setConfirmedExecution(null); }, [stateReadFailed]);
  const blocked = execution.executionState === "blocked";
  const cancelRequested = execution.executionState === "cancel_requested";
  const cancellable = ["queued", "running", "blocked"].includes(execution.executionState);
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">進行中の実行</span>
        <AsynchronousExecutionStatePill state={execution.executionState} startedAt={execution.startedAt} />
        <Link to={`/console/asynchronous-executions/${execution.executionId}${kintoneAppId ? `?kintoneAppId=${encodeURIComponent(kintoneAppId)}` : ""}`} className="underline">詳細</Link>
      </div>
      {!stateVerified ? stateReadFailed ? <div className="space-y-2">
        <p role="alert">ジョブの現在状態を確認できません。表示は最終取得時の状態です。再取得が成功するまで再開・取消はできません。</p>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void onRefresh()}>ジョブ状態を再取得</Button>
      </div> : <p role="status">ジョブ状態を更新しています。表示は最終取得時の状態です。</p> : null}
      <p>
        {cancelRequested
          ? "取消を処理中です。完了までお待ちください。"
          : blocked
          ? `要対応を解消してから「${resumeLabel}」してください。処理を中止する場合は「${cancelLabel}」を選択してください。`
          : `完了するまで次の処理は開始できません。中止する場合は「${cancelLabel}」を選択してください。`}
      </p>
      {execution.reasonCode ? <p>{asynchronousExecutionReasonLabel(execution.reasonCode)}</p> : null}
      <AsynchronousExecutionStatusGuidance execution={execution} />
      {(cancellable || cancelRequested) ? <p className="text-xs">取消しても、成功済みの更新は元に戻りません。</p> : null}
      {blocked ? (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={resolutionConfirmed} disabled={actionsDisabled} onChange={(event) => setConfirmedExecution(event.target.checked ? executionIdentity : null)} />
          阻害要因の解消を確認しました
        </label>
      ) : null}
      <div className="flex gap-2">
        {blocked ? (
          <Button type="button" size="sm" variant="outline" disabled={actionsDisabled || !resolutionConfirmed} onClick={() => {
            setConfirmedExecution(null);
            void onResume();
          }}>
            {resumeLabel}
          </Button>
        ) : null}
        {cancellable ? (
          <Button type="button" size="sm" variant="outline" disabled={actionsDisabled} onClick={() => void onCancel()}>
            {cancelLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
